/**
 * The Z5 tool panel (S5): header (the tool's label + Close), body (the tool).
 *
 *   column  beside <main>, squeezing it; resizable from its left edge (drag, or ←/→ on the
 *           handle) between 280 and the most the room allows (≤ 480); the width is committed
 *           to panes.toolPanelWidth once per drag / key, through usePreferences (its debounce
 *           and 409 merge)
 *   sheet   360 px over the record, anchored right, no scrim; an outside click closes it
 *
 * Opening moves focus to the heading; Esc (anywhere in the panel) and Close close it and focus
 * goes back to what opened it. An open tool that is unavailable here (a record tool with no
 * record) shows why instead of its body. Tokens only.
 */
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { TOOL_COLUMN_MIN, TOOL_RESIZE_STEP } from './toolGeometry';
import './tools.css';

export default function ToolPanel({
  tool, ctx, mode, width, maxWidth, sheetRight = 0, onClose, onResize, onResizeEnd, onOutside
}) {
  const panelRef = useRef(null);
  const headingRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (headingRef.current) headingRef.current.focus({ preventScroll: true });
  }, [tool.id]);

  // The sheet: a tap outside it (and outside the controls that open tools) closes it.
  useEffect(() => {
    if (mode !== 'sheet') return undefined;
    const onDown = (e) => {
      const el = panelRef.current;
      if (el && el.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-tool-trigger]')) return;
      onOutside();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [mode, onOutside]);

  const clamp = (w) => Math.min(maxWidth, Math.max(TOOL_COLUMN_MIN, Math.round(w)));

  const startDrag = (e) => {
    e.preventDefault();
    drag.current = { x: e.clientX, width };
    const move = (ev) => {
      if (!drag.current) return;
      // The handle is on the left edge: moving left widens the panel.
      onResize(clamp(drag.current.width + (drag.current.x - ev.clientX)));
    };
    const up = (ev) => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      if (!drag.current) return;
      const final = clamp(drag.current.width + (drag.current.x - ev.clientX));
      drag.current = null;
      onResizeEnd(final);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const onHandleKey = (e) => {
    const delta = e.key === 'ArrowLeft' ? TOOL_RESIZE_STEP : e.key === 'ArrowRight' ? -TOOL_RESIZE_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    onResizeEnd(clamp(width + delta));
  };

  const available = tool.availableFor(ctx);
  const Body = tool.component;

  return (
    <aside
      ref={panelRef}
      aria-label={`${tool.label} tool`}
      data-mode={mode}
      className={`tool-panel tool-panel-${mode}`}
      style={mode === 'sheet' ? { width: `${width}px`, right: `${sheetRight}px` } : { width: `${width}px` }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      {mode === 'column' && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the tool panel"
          aria-valuenow={width}
          aria-valuemin={TOOL_COLUMN_MIN}
          aria-valuemax={maxWidth}
          tabIndex={0}
          className="tool-panel-resize"
          onMouseDown={startDrag}
          onKeyDown={onHandleKey}
        />
      )}
      <div className="tool-panel-header">
        <h2 ref={headingRef} tabIndex={-1} className="tool-panel-title">{tool.label}</h2>
        <button type="button" className="tool-panel-close" aria-label={`Close ${tool.label}`} onClick={onClose}>
          <X aria-hidden="true" />
        </button>
      </div>
      <div className="tool-panel-body">
        {available === true ? <Body ctx={ctx} /> : <p className="tool-panel-unavailable">{available}</p>}
      </div>
    </aside>
  );
}
